import { FormHelperText, SelectChangeEvent } from '@material-ui/core';
// @mui/material から Box、FormControl、InputLabel、MenuItem コンポーネントをインポート
import { Box, FormControl, InputLabel, MenuItem } from '@mui/material';
import Select from '@mui/material/Select';
// React をインポート
import React from 'react';
// react-hook-form から ControllerとUseFormReturn フックをインポート
import { Controller, UseFormReturn } from 'react-hook-form';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';

type Option = {
  value: string;
  label: string;
};
type RHFSelect = {
  name: string;
  label?: string;
  methods: UseFormReturn;
  defaultValue?: string[];
  rules?: Object;
  handleOnChange?: (value: string[], name: string) => void;
  children?: React.ReactNode;
  disabled?: boolean;
  height?: number;
  muiSize?: 'small' | 'medium';
  options: Option[];
};
const RHFMultipleDropdown = ({
  name,
  label,
  methods,
  defaultValue = [],
  rules,
  handleOnChange,
  children,
  disabled,
  height = 40,
  muiSize = 'medium',
  options,
  ...props
}: RHFSelect) => {
  const labelMap = React.useMemo(() => {
    return new Map(options.map((o) => [o.value, o.label]));
  }, [options]);
  // 翻訳関数を取得
  const { t } = useTranslation();
  return (
    <FormControl sx={{ minWidth: 150 }} fullWidth>
      <InputLabel>{label}</InputLabel>
      <Controller
        // フィールド名
        name={name}
        // フォーム制御オブジェクト
        control={methods.control}
        // バリデーションルール
        rules={rules}
        // デフォルト値
        defaultValue={defaultValue}
        render={({ field, fieldState: { error } }) => (
          <>
            <Select
              // native（ネイティブ）
              native={false}
              // 複数選択（ふくすうせんたく）
              multiple={true}
              // 幅を100%にする
              fullWidth={true}
              // サイズ
              size={muiSize}
              // 表示ラベル
              label={label}
              // 無効化
              disabled={disabled}
              // エラー
              error={!!error}
              // 選択値
              value={Array.isArray(field.value) ? field.value : []}
              // ドロップダウンメニューの設定
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                PaperProps: {
                  style: {
                    maxHeight: height,
                    overflowY: 'auto',
                  },
                  sx: {
                    mt: 1,
                    '& .MuiMenuItem-root': {
                      transition: 'background-color 120ms ease',
                    },
                    '& .MuiMenuItem-root:hover': {
                      backgroundColor: '#e3f2fd',
                    },
                    '& .MuiMenuItem-root.Mui-selected': {
                      backgroundColor: '#bbdefb !important',
                    },
                    '& .MuiMenuItem-root.Mui-selected:hover': {
                      backgroundColor: '#90caf9 !important',
                    },
                  },
                },
              }}
              // 変更イベント
              onChange={(event: SelectChangeEvent<typeof field.value>) => {
                const value = event.target.value as string[];
                // 全選択確認
                if (value.includes('ALL')) {
                  // 全選択に変更
                  if (value[value.length - 1] === 'ALL') {
                    const newValue = ['ALL'] as string[];
                    field.onChange(newValue);
                    handleOnChange?.(newValue, name);
                  } else {
                    // 全選択からその他値に変更
                    const newValue = value.filter((v) => v !== 'ALL');
                    field.onChange(newValue);
                    handleOnChange?.(newValue, name);
                  }
                } else {
                  // 全選択以外選択
                  field.onChange(value);
                  handleOnChange?.(value, name);
                }
              }}
              // 選択された値の表示形式を変更する
              renderValue={(selected) => {
                const values = selected as string[];
                // 選択項目
                let text = values.map((v) => labelMap.get(v) ?? v).join(', ');
                if (values.length === 1 && values.includes('ALL')) {
                  // 全選択
                  text = values.map((v) => t('enqueteCalendarList.select.all') ?? v).join(', ');
                }
                if (!values.length) {
                  return <span style={{ color: '#aaa' }}>{label ?? 'Select'}</span>;
                }
                // 選択された値の表示JSXを返す
                return (
                  <Box
                    sx={{
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                    title={text}>
                    {text}
                  </Box>
                );
              }}
              sx={{
                height: 40,
                width: '100%',
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  paddingTop: 0,
                  paddingBottom: 0,
                },
              }}>
              {/* 選択肢：全選択 */}
              <MenuItem key="ALL" value="ALL">
                {t('enqueteCalendarList.select.all')}
              </MenuItem>
              {/* 選択肢 */}
              {options.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText error={!!error?.message}>{error?.message}</FormHelperText>
          </>
        )}
      />
    </FormControl>
  );
};

export default RHFMultipleDropdown;
